import { z } from 'zod';
import { TicketStatus, ticketStatusSchema } from '../enums.js';
/**
 * The job dashboard's tabs, derived from the lifecycle stages rather than invented.
 *
 * One exhaustive mapping, here and nowhere else. A status-to-tab mapping in the client
 * and a status-group filter on the server would drift, and the symptom would be a job
 * that exists in the database, passes every permission check, and appears in **no tab
 * at all** — which from the outside looks like the job was never created, so nobody
 * would report it. Keyed on `TicketStatus` as a `Record`, so a status added to the enum
 * without a home here is a compile error, and the test beside this file fails too.
 *
 * The groups follow the transition table in `ticket-lifecycle.ts`:
 *
 * - OPEN and ESCALATED are both "nobody has started this". ESCALATED is OPEN that
 *   breached its claim window — the same work, more urgent, and it belongs beside it.
 * - ON_HOLD is IN_PROGRESS paused. It is reachable only from IN_PROGRESS and returns
 *   there. Same stage.
 * - RESOLVED and VERIFIED are live manager work, not history. D36 made verify and close
 *   separate steps precisely to create that queue: RESOLVED awaits verification,
 *   VERIFIED awaits administrative closure. Filing them under Closed would delete the
 *   verification queue, and nobody would notice until jobs stopped being signed off.
 * - CANCELLED sits with CLOSED. Finished work, finished differently (D27).
 *
 * Lower-case on the wire like `scope`: these are query-string values, not stored enums.
 */
export const TicketStatusGroup = {
    New: 'new',
    InProgress: 'in-progress',
    ToVerify: 'to-verify',
    Closed: 'closed',
};
export const ticketStatusGroupSchema = z.enum([
    TicketStatusGroup.New,
    TicketStatusGroup.InProgress,
    TicketStatusGroup.ToVerify,
    TicketStatusGroup.Closed,
]);
/** The tabs in the order the dashboard shows them: the lifecycle, left to right. */
export const TICKET_STATUS_GROUPS = ticketStatusGroupSchema.options;
/** Every status, and the one tab it lives in. */
export const TICKET_STATUS_GROUP_OF = {
    [TicketStatus.Open]: TicketStatusGroup.New,
    [TicketStatus.Escalated]: TicketStatusGroup.New,
    [TicketStatus.InProgress]: TicketStatusGroup.InProgress,
    [TicketStatus.OnHold]: TicketStatusGroup.InProgress,
    [TicketStatus.Resolved]: TicketStatusGroup.ToVerify,
    [TicketStatus.Verified]: TicketStatusGroup.ToVerify,
    [TicketStatus.Closed]: TicketStatusGroup.Closed,
    [TicketStatus.Cancelled]: TicketStatusGroup.Closed,
};
export function statusGroupOf(status) {
    return TICKET_STATUS_GROUP_OF[status];
}
/**
 * The statuses a tab shows, derived from the mapping above rather than listed a second
 * time — a second list is exactly the drift this module exists to prevent. The server
 * resolves `?group=` through this; the client never needs to, but may.
 */
export function statusesInGroup(group) {
    return ticketStatusSchema.options.filter((status) => TICKET_STATUS_GROUP_OF[status] === group);
}
/** i18n keys for the tab labels — never raw copy. */
export const ticketStatusGroupLabelKey = {
    [TicketStatusGroup.New]: 'queue.tab.new',
    [TicketStatusGroup.InProgress]: 'queue.tab.inProgress',
    [TicketStatusGroup.ToVerify]: 'queue.tab.toVerify',
    [TicketStatusGroup.Closed]: 'queue.tab.closed',
};
//# sourceMappingURL=status-groups.js.map